import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNotifications } from '../../context/NotificationContext'
import { useQueryClient } from '@tanstack/react-query'
import { chatKeys } from './api'

// Live delivery for chat rides the same authenticated socket the notification
// bell already keeps open (context/NotificationContext.tsx). The server only
// lets a socket join a conversation that the same user could also read over
// REST - see apps/api/src/ws/socket-server.ts - so a room subscription can't
// reveal a conversation the viewer isn't in.

export function useChatRealtime(conversationIds: string[]) {
  const { socket, connected } = useNotifications()
  const queryClient = useQueryClient()
  const joinedRef = useRef<Set<string>>(new Set())
  const key = useMemo(() => [...conversationIds].sort().join(','), [conversationIds])

  useEffect(() => {
    if (!socket || !connected) return
    socket.emit('goodday:joinChat')

    const refreshList = () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations })
    const onMessage = (payload: { conversationId: string }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(payload.conversationId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations })
    }

    socket.on('goodday:chat:message', onMessage)
    socket.on('goodday:chat:conversation', refreshList)
    return () => {
      socket.off('goodday:chat:message', onMessage)
      socket.off('goodday:chat:conversation', refreshList)
    }
  }, [socket, connected, queryClient])

  useEffect(() => {
    if (!socket || !connected) return
    const ids = key ? key.split(',') : []
    joinedRef.current.forEach((id) => {
      if (!ids.includes(id)) {
        socket.emit('goodday:leaveConversation', id)
        joinedRef.current.delete(id)
      }
    })
    ids.forEach((id) => {
      if (!joinedRef.current.has(id)) {
        joinedRef.current.add(id)
        socket.emit('goodday:joinConversation', id)
      }
    })
  }, [socket, connected, key])

  useEffect(() => {
    const joined = joinedRef.current
    return () => {
      joined.forEach((id) => socket?.emit('goodday:leaveConversation', id))
      joined.clear()
    }
  }, [socket])
}
